import React, { useState } from "react";
import { Modal, ModalHeader, ModalBody, Row, Col, Badge, Form, Input, Button, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { addResaleInventoryLogApi } from "../../apiServices/resaleInventoryServices";
import { formatIndianNumber } from "../../utils/indianNumberFormatter";

const getStatusBadge = (status) => {
  switch (status) {
    case "Available":
      return "success";
    case "Under Negotiation":
      return "warning";
    case "Token Received":
      return "info";
    case "Sold":
      return "danger";
    case "On Hold":
      return "secondary";
    default:
      return "primary";
  }
};

const ResaleInventoryDetailsModal = ({ isOpen, toggle, item, onLogAdded }) => {
  const [newLog, setNewLog] = useState("");
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [localLogs, setLocalLogs] = useState(item?.logs || []);

  React.useEffect(() => {
    if (item?.logs) {
      setLocalLogs(item.logs);
    }
  }, [item]);

  if (!item) return null;

  const handleAddLog = async (e) => {
    e.preventDefault();
    if (!newLog.trim()) return;

    try {
      setLoading(true);
      const res = await addResaleInventoryLogApi(item._id, newLog.trim());
      toast.success("Log remark added successfully!");
      setNewLog("");

      const updatedItem = res?.data;
      if (updatedItem && updatedItem.logs) {
        setLocalLogs(updatedItem.logs);
      }

      if (onLogAdded) onLogAdded(updatedItem);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add log");
    } finally {
      setLoading(false);
    }
  };

  const details = item.property_details || {};
  const location = details.location || {};
  const images = item.media?.images || [];
  const videos = item.media?.videos || [];

  return (
    <React.Fragment>
      <Modal isOpen={isOpen} toggle={toggle} size="lg" centered scrollable>
        <ModalHeader toggle={toggle} className="border-bottom">
          <span className="fw-bold text-primary">Resale Property Details</span>
        </ModalHeader>
        <ModalBody style={{ maxHeight: "calc(80vh - 120px)", overflowY: "auto" }} className="p-3 p-md-4">
          
          {/* Hero Header Card */}
          <div className="p-3 bg-soft-primary rounded-3 border border-primary-subtle mb-4">
            <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
              <div>
                <div className="d-flex align-items-center gap-2">
                  <div className="avatar-xs">
                    <span className="avatar-title rounded-circle bg-primary text-white font-size-14">
                      {item.name?.charAt(0)?.toUpperCase() || "P"}
                    </span>
                  </div>
                  <h5 className="mb-0 text-primary fw-bold font-size-17">{item.name}</h5>
                </div>
                <div className="d-flex flex-wrap gap-2 align-items-center mt-2 font-size-12">
                  <a
                    href={`tel:${item.contact_number}`}
                    className="btn btn-sm btn-white border text-dark py-1 px-2 font-size-12 shadow-sm"
                  >
                    <i className="mdi mdi-phone me-1 text-success"></i>
                    {item.contact_number}
                  </a>
                  {item.email && (
                    <a
                      href={`mailto:${item.email}`}
                      className="btn btn-sm btn-white border text-dark py-1 px-2 font-size-12 shadow-sm text-truncate"
                      style={{ maxWidth: "220px" }}
                    >
                      <i className="mdi mdi-email-outline me-1 text-info"></i>
                      {item.email}
                    </a>
                  )}
                </div>
              </div>

              <div className="text-sm-end mt-2 mt-sm-0">
                <div className="h4 fw-bold text-success mb-1">
                  {formatIndianNumber(item.sell_price)}
                </div>
                <Badge color={getStatusBadge(item.sale_status)} className="px-3 py-1 font-size-12 rounded-pill">
                  {item.sale_status}
                </Badge>
              </div>
            </div>
          </div>

          {/* Specifications Grid */}
          <h6 className="fw-bold mb-3 text-secondary font-size-14">
            <i className="mdi mdi-office-building-cog me-1 text-primary"></i> Specifications
          </h6>
          <Row className="g-2 g-md-3 mb-4">
            <Col md={3} xs={6}>
              <div className="p-2 p-md-3 bg-light rounded-3 text-center border h-100 d-flex flex-column justify-content-center">
                <small className="text-muted d-block font-size-11 text-uppercase fw-semibold mb-1">
                  <i className="mdi mdi-home-city-outline me-1"></i> Type
                </small>
                <span className="fw-bold text-dark font-size-13">{details.type || "N/A"}</span>
              </div>
            </Col>
            <Col md={3} xs={6}>
              <div className="p-2 p-md-3 bg-light rounded-3 text-center border h-100 d-flex flex-column justify-content-center">
                <small className="text-muted d-block font-size-11 text-uppercase fw-semibold mb-1">
                  <i className="mdi mdi-bed-double-outline me-1"></i> BHK Config
                </small>
                <span className="fw-bold text-dark font-size-13">{details.bhk || "N/A"}</span>
              </div>
            </Col>
            <Col md={3} xs={6}>
              <div className="p-2 p-md-3 bg-light rounded-3 text-center border h-100 d-flex flex-column justify-content-center">
                <small className="text-muted d-block font-size-11 text-uppercase fw-semibold mb-1">
                  <i className="mdi mdi-ruler-square me-1"></i> Area Size
                </small>
                <span className="fw-bold text-dark font-size-13">
                  {details.size_sqft ? `${details.size_sqft} Sq. Ft.` : "N/A"}
                </span>
              </div>
            </Col>
            <Col md={3} xs={6}>
              <div className="p-2 p-md-3 bg-light rounded-3 text-center border h-100 d-flex flex-column justify-content-center">
                <small className="text-muted d-block font-size-11 text-uppercase fw-semibold mb-1">
                  <i className="mdi mdi-layers-triple me-1"></i> Floor
                </small>
                <span className="fw-bold text-dark font-size-13">{details.floor || "N/A"}</span>
              </div>
            </Col>
            <Col md={3} xs={6}>
              <div className="p-2 p-md-3 bg-light rounded-3 text-center border h-100 d-flex flex-column justify-content-center">
                <small className="text-muted d-block font-size-11 text-uppercase fw-semibold mb-1">
                  <i className="mdi mdi-sofa-outline me-1"></i> Furnishing
                </small>
                <span className="fw-bold text-dark font-size-13">{details.furnishing || "N/A"}</span>
              </div>
            </Col>
            <Col md={3} xs={6}>
              <div className="p-2 p-md-3 bg-light rounded-3 text-center border h-100 d-flex flex-column justify-content-center">
                <small className="text-muted d-block font-size-11 text-uppercase fw-semibold mb-1">
                  <i className="mdi mdi-compass-outline me-1"></i> Facing
                </small>
                <span className="fw-bold text-dark font-size-13">{details.facing || "N/A"}</span>
              </div>
            </Col>
            <Col md={3} xs={6}>
              <div
                className="p-2 p-md-3 bg-light rounded-3 text-center border h-100 d-flex flex-column justify-content-center"
                style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
              >
                <small className="text-muted d-block font-size-11 text-uppercase fw-semibold mb-1">
                  <i className="mdi mdi-map-marker-outline me-1"></i> City / Sector
                </small>
                <span className="fw-bold text-dark font-size-13">
                  {location.sector || location.city
                    ? `${location.sector || ""}, ${location.city || ""}`
                    : "N/A"}
                </span>
              </div>
            </Col>
            <Col md={3} xs={6}>
              <div className="p-2 p-md-3 bg-light rounded-3 text-center border h-100 d-flex flex-column justify-content-center">
                <small className="text-muted d-block font-size-11 text-uppercase fw-semibold mb-1">
                  <i className="mdi mdi-account-outline me-1"></i> Created By
                </small>
                <span className="fw-bold text-dark font-size-13">{item.createdBy?.name || "System"}</span>
              </div>
            </Col>
          </Row>

          {/* Location Details */}
          <div className="mb-4 p-3 border rounded-3 bg-white shadow-sm">
            <h6 className="fw-bold text-secondary mb-2 font-size-14">
              <i className="mdi mdi-map-marker-radius me-1 text-danger"></i> Location Details
            </h6>
            <p className="mb-2 text-muted font-size-13">
              {location.address || "No detailed address provided."}
            </p>
            {location.googleMapLink && (
              <a
                href={location.googleMapLink}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-soft-primary btn-sm rounded-pill font-size-12"
              >
                <i className="mdi mdi-google-maps me-1"></i> Open Google Maps
              </a>
            )}
          </div>

          {/* Scrollable Media Gallery & Video Player */}
          {(images.length > 0 || videos.length > 0) && (
            <div className="mb-4 p-3 border rounded-3 bg-white shadow-sm">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="fw-bold text-secondary mb-0 font-size-14">
                  <i className="mdi mdi-file-image-outline me-1 text-info"></i> Media Gallery ({images.length} Photos, {videos.length} Videos)
                </h6>
                <small className="text-muted font-size-11">Scroll horizontally to view all</small>
              </div>

              {/* Scrollable Gallery Container */}
              <div
                className="d-flex gap-3 overflow-auto pb-2 px-1"
                style={{
                  scrollBehavior: "smooth",
                }}
              >
                {/* Image Cards */}
                {images.map((img, i) => (
                  <div key={`img-${i}`} style={{ minWidth: "150px", width: "150px", flexShrink: 0 }}>
                    <div
                      className="position-relative overflow-hidden rounded-3 shadow-sm border bg-light"
                      style={{ height: "100px", cursor: "pointer" }}
                      onClick={() => setPreviewImage(img)}
                    >
                      <img
                        src={img}
                        alt={`Property ${i + 1}`}
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                      <div
                        className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-60 text-white font-size-10 px-2 py-1 d-flex justify-content-between align-items-center"
                      >
                        <span>Photo {i + 1}</span>
                        <i className="mdi mdi-magnify-plus-outline"></i>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Video Cards */}
                {videos.map((vid, i) => (
                  <div key={`vid-${i}`} style={{ minWidth: "200px", width: "200px", flexShrink: 0 }}>
                    <div
                      className="position-relative overflow-hidden rounded-3 shadow-sm border bg-black"
                      style={{ height: "100px" }}
                    >
                      <video
                        src={vid}
                        controls
                        className="w-100 h-100"
                        style={{ objectFit: "cover" }}
                      />
                    </div>
                    <div className="text-center font-size-11 text-muted mt-1 fw-semibold">
                      <i className="mdi mdi-video me-1 text-danger"></i> Video {i + 1}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Property Activity Logs Timeline */}
          <div className="p-3 border rounded-3 bg-white shadow-sm">
            <h6 className="fw-bold text-secondary mb-3 font-size-14">
              <i className="mdi mdi-history me-1 text-warning"></i> Property Activity Logs
            </h6>

            <Form onSubmit={handleAddLog} className="mb-4">
              <div className="d-flex gap-2">
                <Input
                  type="text"
                  value={newLog}
                  onChange={(e) => setNewLog(e.target.value)}
                  placeholder="Add activity remark or note (e.g. Client requested callback...)"
                  className="form-control-sm"
                />
                <Button color="primary" type="submit" className="btn-sm text-nowrap px-3" disabled={loading || !newLog.trim()}>
                  {loading ? <Spinner size="sm" /> : "Add Log"}
                </Button>
              </div>
            </Form>

            <div
              className="timeline border-start ms-2 ps-3"
              style={{ maxHeight: "220px", overflowY: "auto" }}
            >
              {localLogs && localLogs.length > 0 ? (
                localLogs.map((log, index) => (
                  <div key={index} className="mb-3 position-relative">
                    <div
                      className="position-absolute bg-primary rounded-circle"
                      style={{
                        width: "10px",
                        height: "10px",
                        left: "-21px",
                        top: "4px",
                      }}
                    ></div>
                    <div className="fw-semibold font-size-13 text-dark">
                      {log.note}
                    </div>
                    <div className="text-muted font-size-11">
                      By {log.createdBy?.name || "User"} •{" "}
                      {new Date(log.createdAt).toLocaleString("en-IN")}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-muted font-size-12">No activity logs recorded yet.</div>
              )}
            </div>
          </div>
        </ModalBody>
      </Modal>

      {/* Lightbox Image Preview Modal */}
      <Modal isOpen={!!previewImage} toggle={() => setPreviewImage(null)} centered size="lg">
        <ModalHeader toggle={() => setPreviewImage(null)}>
          Property Photo Preview
        </ModalHeader>
        <ModalBody className="p-0 text-center bg-black">
          {previewImage && (
            <img
              src={previewImage}
              alt="Property Preview"
              style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};

export default ResaleInventoryDetailsModal;
